<!-- eslint-disable vue/no-deprecated-v-bind-sync -->
<template>
  <v-row class="mb-2">
    <v-col cols="7" class="mx-n4 mt-1">
      <v-card-title>Afficher le contenu</v-card-title>
    </v-col>
    <v-col cols="10" class="mt-n8">
      <v-select
        v-model="newAction.display_type"
        item-text="label"
        item-value="value"
        item-color="info"
        menu-props="light"
        label="Type de contenu"
        :items="contentTypesOptions"
        color="blue"
        hide-details
        :rules="[(v) => !!v || 'Required']"
        @change="changeDisplayType(newAction.display_type)"
      />
    </v-col>
    <v-col v-if="showProductSearch" cols="10">
      <v-autocomplete
        v-model="newAction.display_content"
        :search-input.sync="queryProductSearch"
        :hide-no-data="loading"
        hide-selected
        :item-text="getFieldText"
        item-value="uuid"
        item-color="info"
        menu-props="light"
        chips
        label="Sélectionner au moins 3 articles"
        :items="productsItems"
        :loading="loading"
        no-filter
        color="blue"
        counter="3"
        clearable
        :rules="[v => !!v || 'Required', v => v.length <= 3 || 'Taille maximale du produit 3']"
        multiple
        @input="queryProductSearch = ''"
        @focus="queryProductSearch = ''; searchAlgolia('')"
      >
        <template #selection="{ attrs, item, index, parent, selected }">
          <v-chip
            v-if="item === Object(item)"
            v-bind="attrs"
            color=" lighten-3`"
            :input-value="selected"
            label
            small
          >
            <span class="pr-2">
              {{ item.name }}
            </span>
            <v-icon
              small
              @click.stop="newAction.display_content.splice(index, 1);"
            >
              $delete
            </v-icon>
          </v-chip>
        </template>
        <template #item="{ item }">
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
            <v-list-item-subtitle>{{ item.package }} / {{ item.uuid }} / {{ item.objectID }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </v-col>
    <v-col v-if="showContentSelect" cols="10">
      <v-autocomplete
        v-model="newAction.display_content"
        :search-input.sync="contentQuery"
        item-text="label"
        item-value="value"
        item-color="info"
        menu-props="light"
        :label="isMultiple ? 'MultipleContent' : 'Contenu'"
        :items="contentItems"
        color="blue"
        hide-details
        clearable
        chips
        :multiple="isMultiple"
        :loading="loading"
        :hide-no-data="loading"
        :rules="[(v) => !!v || 'Required']"
        :filter="filter"
        @focus="searchMerchContent()"
      />
    </v-col>
    <v-col v-if="positionOptions.length > 0" cols="10">
      <v-row no-gutters class="justify-space-between">
        <v-col cols="9">
          <v-select
            v-model="displayPosition"
            item-text="label"
            item-value="value"
            item-color="info"
            menu-props="light"
            label="Position"
            :items="positionOptions"
            color="blue"
            clearable
            :rules="[(v) => !!v || 'Required']"
          />
        </v-col>
        <v-col cols="2">
          <v-text-field
            v-if="isSearchPosition"
            v-model="searchPosition"
            label="Number"
            type="number"
            color="blue"
            :rules="[v => v > 0 || 'Required']"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>
<script setup>
import { ref, computed, watch, onMounted, defineEmits, reactive } from 'vue';
import gql from 'graphql-tag';

import { useStore } from 'vue2-helpers/vuex';

import { useApolloClient } from '@vue/apollo-composable';
import { getGraphqlSchema } from '@/graphql/index';

const { openSearchLiveContentFilters } = require('@/utils/merch');

const store = useStore();
const { resolveClient } = useApolloClient();

// Define props
const props = defineProps({
  action: { type: Object, required: true },
  actionsModuleConfiguration: { type: Object, default: null }
});

const newAction = ref(props.action);

const queryProductSearch = ref(null);
const products = ref([]);

const loading = ref(false);

const contentQuery = ref('');

// DEFAULT CONFIGURATION
const contentTypesOptions = ref([
  { label: 'Banner', value: 'banner' },
  { label: 'Popin', value: 'popin' },
  { label: 'Sticky', value: 'sticky' },
  { label: 'Product on Search', value: 'product_autocomplete_search' },
  { label: 'CTA', value: 'cta' }
]);

if (props.actionsModuleConfiguration && props.actionsModuleConfiguration.options) {
  contentTypesOptions.value = props.actionsModuleConfiguration.options;
}

const contentOptions = ref([]);

const contentItems = computed(() => contentOptions.value);

const displayPosition = computed({
  get: () => {
    if (newAction.value.display_position && newAction.value.display_position.includes('search-position')) {
      return 'search-position';
    }

    return newAction.value.display_position;
  },
  set: position => {
    newAction.value.display_position = position;
  }
});

const isSearchPosition = computed(() => displayPosition.value && displayPosition.value.includes('search-position'));

const searchPosition = computed({
  get: () => {
    if (newAction.value.display_position) {
      const replace = newAction.value.display_position === 'search-position' ? newAction.value.display_position : 'search-position-';
      const searchPositionNumber = newAction.value.display_position.replace(replace, '');
      return searchPositionNumber !== '' ? searchPositionNumber : 1;
    }

    return null;
  },
  set: value => newAction.value.display_position = `search-position-${value}`
});

const positionOptions = computed(() => {
  if (newAction.value.display_type === 'banner') {
    return store.getters.ruleBannerPositions;
  }

  if (newAction.value.display_type === 'cta') {
    return ['search-top'];
  }

  return [];
});

const showContentSelect = computed(() => {
  if (props.actionsModuleConfiguration && props.actionsModuleConfiguration.options) {
    const allowSearch = props.actionsModuleConfiguration.options.filter(config => config.searchContent).map(config => config.value);
    if (allowSearch.includes(newAction.value.display_type)) return true;
    // DEFAULT CONFIGURATION
  } else if (['popin', 'banner', 'sticky', 'cta'].includes(newAction.value.display_type)) {
    return true;
  }
  return false;
});

const isMultiple = computed(() => ['cta'].includes(newAction.value.display_type));

const showProductSearch = computed(() => newAction.value.display_type === 'product_autocomplete_search');

const getFieldText = item => `${item.name} | ${item.uuid}`;

let observable = null;

const searchMerchContent = async (uuids = null) => {
  contentOptions.value = [];

  loading.value = true;

  const filters = { query: { bool: { filter: openSearchLiveContentFilters() } } };

  filters.query.bool.filter.push({
    terms: {
      wtype: [
        newAction.value.display_type
      ]
    }
  });

  if (uuids) {
    filters.query.bool.filter.push({ terms: { 'uuid.raw': uuids } });
  }

  filters.query.bool.must = {
    simple_query_string: {
      query: '*',
      fields: [
        'wtitle',
        'uuid'
      ]
    }
  };

  if (observable) {
    observable.unsubscribe();
  }

  const apollo = resolveClient();
  const watchedQuery = apollo.watchQuery({
    query: getGraphqlSchema('openSearch'),
    variables: {
      input: {
        from: 0,
        size: 1000,
        query: filters.query
      }
    },
    context: { headers: { mode: 'live' } }
    // fetchPolicy: 'no-cache'
  });

  // Subscribe to it, and do something with the data
  observable = watchedQuery.subscribe(({ data }) => {
    contentOptions.value = data.search.items.map(value => ({ value: value.uuid, label: value.wtitle }));
    loading.value = false;
  });
};

const searchAlgolia = (search, uuids = null) => {
  loading.value = true;

  const contextFilters = store.getters.contexAndAgencyFilters;

  if (observable) {
    observable.unsubscribe();
  }

  const variables = {
    query: search || '',
    args: {
      enableRules: false,
      filters: contextFilters || '',
      attributesToRetrieve: [
        'name',
        'uuid',
        'context',
        'package'
      ]
    }
  };

  if (uuids && uuids.length) {
    if (variables.args.filters) {
      variables.args.filters += ' AND ';
    }

    variables.args.filters += `(uuid:${uuids.toString().replace(/,/g, ' OR uuid:')})`;
  }

  const apollo = resolveClient();
  const watchedQuery = apollo.watchQuery({
    query: getGraphqlSchema('searchAlgolia'),
    variables: variables
  });

  // Subscribe to it, and do something with the data
  observable = watchedQuery.subscribe(({ data }) => {
    loading.value = false;

    products.value = data.searchAlgolia.items.map(v => ({
      uuid: v.uuid,
      name: v.name,
      package: v.package,
      objectID: v.objectID
    }));
  });
};

const productsItems = computed(() => {
  if (newAction.value.display_content) {
    return products.value;
  }

  return products.value;
});

watch(() => queryProductSearch.value, val => {
  searchAlgolia(val);
});

const filter = (item, queryText, itemText) => {
  if (item.header) return false;
  // console.log(item.label, queryText, itemText);

  const hasValue = val => (val != null ? val : '');

  const text = hasValue(itemText);
  const query = hasValue(queryText);

  return text.toString()
    .toLowerCase()
    .indexOf(query.toString().toLowerCase()) > -1;
};

const emit = defineEmits(['update:action']);

watch(() => newAction.value, val => {
  emit('update:action', val);
}, { deep: true });

const changeDisplayType = async val => {
  const value = { key: newAction.value.key, type: newAction.value.type, display_type: val };
  if (showContentSelect.value) {
    value.display_content = null;
  }

  if (showProductSearch.value) {
    value.display_content = [];
  }

  if (positionOptions.value.length > 0) {
    value.display_position = null;
  }

  newAction.value = value;
};

onMounted(() => {
  if (showContentSelect.value && newAction.value.display_content) {
    searchMerchContent([newAction.value.display_content]);
  }

  if (showProductSearch.value && newAction.value.display_content && newAction.value.display_content.length) {
    searchAlgolia('', newAction.value.display_content);
  }
});

</script>

<style>
</style>
